import { createPlateEditor } from 'platejs/react'
import type {
  AnyPlatePlugin,
  CreatePlateEditorOptions,
  PlateCorePlugin,
} from 'platejs/react'

import { createContentGenerationPlugin } from '~publish/legacy/ai/plugins/content-generation'
import {
  SERVICE_BLUESKY,
  SERVICE_FACEBOOK,
  SERVICE_GOOGLEBUSINESS,
  SERVICE_INSTAGRAM,
  SERVICE_LINKEDIN,
  SERVICE_MASTODON,
  SERVICE_PINTEREST,
  SERVICE_STARTPAGE,
  SERVICE_SUBSTACK,
  SERVICE_THREADS,
  SERVICE_TIKTOK,
  SERVICE_TWITTER,
  SERVICE_YOUTUBE,
} from '~publish/legacy/constants'
import type { ServiceDefinition } from '~publish/legacy/constants/services/types'
import type { BufferEditorOptions } from '~publish/legacy/editor/BufferEditor/types'
import type {
  BufferEditor,
  BufferValue,
} from '~publish/legacy/editor/BufferEditor/types.plate'
import { createCharacterLimitHighlightPlugin } from '~publish/legacy/editor/plugins/character-limit-highlight'
import { createNewThreadPostPlugin } from '~publish/legacy/editor/plugins/createNewThreadPlugin'
import { createTextSyncPlugin } from '~publish/legacy/editor/plugins/createTextSyncPlugin'
import { createFacebookMentionPlugin } from '~publish/legacy/editor/plugins/facebook-mention'
import { createGenerateContentPlugin } from '~publish/legacy/editor/plugins/generate-content'
import { createLinkedInAnnotationPlugin } from '~publish/legacy/editor/plugins/linkedin-annotations/createLinkedInAnnotationPlugin'

import { createCoreComposerPlugins } from './createCoreComposerEditor'

const createPlugins = (
  service: ServiceDefinition,
  plugins: AnyPlatePlugin[] = [],
): PlateCorePlugin[] => {
  return createCoreComposerPlugins({
    afterEmoji: [
      createCharacterLimitHighlightPlugin(),
      createGenerateContentPlugin(),
      createContentGenerationPlugin(),
    ],
    afterTextSanitization: plugins,
    trailing: [
      createTextSyncPlugin(),
      ...(service.supportsThread() ? [createNewThreadPostPlugin()] : []),
    ],
  })
}

export const createTwitterEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_TWITTER,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createInstagramEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_INSTAGRAM,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createLinkedInEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_LINKEDIN,
    plugins: createPlugins(service, [createLinkedInAnnotationPlugin()]),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createFacebookEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_FACEBOOK,
    plugins: createPlugins(service, [createFacebookMentionPlugin()]),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createPinterestEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_PINTEREST,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createMastodonEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_MASTODON,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createYoutubeEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_YOUTUBE,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createThreadsEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_THREADS,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createBlueskyEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_BLUESKY,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createSubstackEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_SUBSTACK,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createTiktokEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_TIKTOK,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createGooglebusinessEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_GOOGLEBUSINESS,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createStartPageEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: SERVICE_STARTPAGE,
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createOmniEditor = (
  service: ServiceDefinition,
  options: CreatePlateEditorOptions<BufferValue> = {},
): BufferEditor => {
  return createPlateEditor<BufferValue>({
    id: 'omni',
    plugins: createPlugins(service),
    nodeId: false,
    ...options,
  }) as unknown as BufferEditor
}

export const createEditorForService = (
  service: ServiceDefinition,
  options?: BufferEditorOptions,
): BufferEditor => {
  switch (service.name) {
    case SERVICE_TWITTER:
      return createTwitterEditor(service, options)
    case SERVICE_INSTAGRAM:
      return createInstagramEditor(service, options)
    case SERVICE_LINKEDIN:
      return createLinkedInEditor(service, options)
    case SERVICE_FACEBOOK:
      return createFacebookEditor(service, options)
    case SERVICE_PINTEREST:
      return createPinterestEditor(service, options)
    case SERVICE_TIKTOK:
      return createTiktokEditor(service, options)
    case SERVICE_GOOGLEBUSINESS:
      return createGooglebusinessEditor(service, options)
    case SERVICE_STARTPAGE:
      return createStartPageEditor(service, options)
    case SERVICE_MASTODON:
      return createMastodonEditor(service, options)
    case SERVICE_YOUTUBE:
      return createYoutubeEditor(service, options)
    case SERVICE_THREADS:
      return createThreadsEditor(service, options)
    case SERVICE_BLUESKY:
      return createBlueskyEditor(service, options)
    case SERVICE_SUBSTACK:
      return createSubstackEditor(service, options)
    case 'omni':
      return createOmniEditor(service, options)
    default:
      throw new Error(
        `Invalid service: Could not create editor for service: ${service?.name}`,
      )
  }
}

export const createNewEditor = (
  service: ServiceDefinition,
  options?: BufferEditorOptions,
): BufferEditor => {
  return createEditorForService(service, options)
}
